// src/components/WealthMethod/WealthMethod.tsx
import React, { JSX } from "react";
import {
  Star,
  Users,
  Shield,
  CheckCircle,
  ArrowRight,
  DollarSign,
  TrendingUp,
  BookOpen,
  MessageCircle,
  Award,
  Zap,
  VideoIcon,
} from "lucide-react";
import bookCover from "../../../assets/images/mockup2.png";
import { useNavigate } from "react-router-dom";

// JSON imports (make sure tsconfig has "resolveJsonModule": true)
import testimonialsData from "../../../data/WealthMethodData/testimonials.json";
import wealthStepsData from "../../../data/WealthMethodData/wealthSteps.json";
import bonusesData from "../../../data/WealthMethodData/bonuses.json";
import brand from "../../../data/WealthMethodData/brand.json";

// types (the file you just added)
import type {
  Testimonial,
  WealthStep,
  Bonus,
  StepLetter,
  BrandData,
} from "../../../data/WealthMethodData/types";

const WealthMethod: React.FC = () => {
  const navigate = useNavigate();

  const brandData = brand as BrandData;
  const spotsLeft = brandData.spotsLeft ?? 23;
  const PRICE = brandData.price ?? 5000;
  const TOTAL_SPOTS = brandData.totalSpots ?? 50;

  const handleClick = () => navigate("checkout");

  // strongly typed icon map to avoid indexing errors
  const iconMap: Record<StepLetter, JSX.Element> = {
    W: <DollarSign className="w-8 h-8" aria-hidden="true" />,
    E: <Users className="w-8 h-8" aria-hidden="true" />,
    A: <Zap className="w-8 h-8" aria-hidden="true" />,
    L: <TrendingUp className="w-8 h-8" aria-hidden="true" />,
    T: <Award className="w-8 h-8" aria-hidden="true" />,
    H: <Star className="w-8 h-8" aria-hidden="true" />,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bgGray via-white to-bgGray">
      {/* Header */}
      <div className="bg-gradient-to-r from-brandNavy to-brandLightNavy text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Finally! A Proven Way to Escape the 9-to-5 Struggle
          </h1>
          <h2 className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
            Discover How Ordinary Nigerians Are Turning Their Knowledge and
            Skills Into ₦947K+ Monthly Income Online (While Still Working Their
            Day Jobs)
          </h2>

          <button
            onClick={handleClick}
            className="w-full bg-brandRed text-white py-4 px-6 rounded-lg text-lg font-bold hover:bg-brandLightNavy transition-colors flex items-center justify-center mb-4"
            aria-label="Get the WEALTH Method now"
          >
            GET THE W.E.A.L.T.H METHOD NOW
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">
            Don't get trapped in Nigeria's Financial Prison
          </h3>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            <p className="text-xl mb-6">
              Hello there! Let me paint a picture you know too well:
            </p>
            <p className="mb-6">
              It's 4:30 AM. Your phone alarm screams at you like a creditor
              demanding payment. You drag yourself out of bed, knowing you'll
              spend 3 hours in Lagos traffic just to reach an office where your
              boss treats you like furniture. Your ₦80,000 salary? Gone before
              the 15th of every month.
            </p>
            <div className="bg-red-50 border-l-4 border-red-400 p-6 my-8">
              <h4 className="text-xl font-semibold mb-4 text-red-800">
                Sound familiar?
              </h4>
              <ul className="space-y-3 text-red-700">
                <li>
                  • Fuel prices jump from ₦165 to ₦900 overnight—your budget
                  scatters like pepper in the wind
                </li>
                <li>
                  • Your landlord threatens eviction while you're calculating if
                  you can afford both rice and transport this week. On top of
                  that, school fees have gone up 3X.
                </li>
                <li>
                  • You watch your mates online living the dream, spending more
                  money while you are cracking your brain on how to survive and
                  you are wondering: "Is this real or another Yahoo story?"
                </li>
                <li>
                  • Every family gathering becomes an interrogation: "When will
                  you buy a car? When will you build a house? When will you
                  marry? Pressure everywhere!"
                </li>
              </ul>
            </div>
            <p className="text-xl font-semibold text-center bg-goldAccent p-6 rounded-lg">
              The brutal truth? While you're trading 12 hours daily for peanuts,
              smart Nigerians are building digital empires without stress.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">
            How I Discovered the ₦947K Monthly Blueprint
          </h3>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-xl mb-6 font-medium">
                Three years ago, I was exactly where you are now.
              </p>
              <p className="mb-6">
                Business had dried up because people could no longer spend the
                way they did before. I had exhausted my savings and was hunting
                for clients for my business while maintaining my 9-5 job. You
                know that feeling when it's like you have to choose between
                fueling your car or feeding your family. The amount of
                calculations that go on in your mind as you budget like your
                life depends on it.
              </p>
              <p className="mb-6">
                I had prayed and done everthing I knew to do. You know you are
                not lazy and everyone tells you it's a matter of time that
                things would get better.
              </p>
              <p className="mb-6">
                But I didn't want to wait for things to get better, I wanted it
                to get better right now, so I challenged myself to find
                solutions.
              </p>

              <p className="mb-8">
                Through sleepless nights, I came across this system that has
                changed my life.
              </p>
              <div className="bg-brandLightNavy bg-opacity-10 border border-lightBorder rounded-lg p-6 text-center">
                <p className="text-lg text-brandNavy">
                  Over 4 months, I learned the system that helped me escape the
                  cycle. I rearranged my business and in one day, I made more
                  sales than I had made in the last 6 months combined.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">
            Let me tell you what I found that changed everything for me
          </h3>
          <div className="gap-8">
            {(testimonialsData as Testimonial[]).map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-bgGray to-white rounded-lg shadow-lg p-6 border border-lightBorder mb-6"
              >
                <div className="flex items-center mb-4">
                  <div>
                    <h4 className="font-bold text-2xl text-gray-800">
                      {testimonial.name}
                    </h4>
                  </div>
                </div>
                <p className="text-gray-700 text-sm italic">
                  "{testimonial.story}"
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-xl font-semibold text-gray-800 bg-goldAccent p-4 rounded-lg inline-block">
              It was like my eyes were just opened. These things were there all
              along and I didn't know
            </p>
          </div>
        </div>
      </section>

      {/* WEALTH Method */}
      <section className="py-16 px-4 bg-gradient-to-br from-brandNavy to-brandLightNavy text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold mb-6">
              Let Me Introduce To You The W.E.A.L.T.H. Method
            </h3>
            <p className="text-2xl opacity-90">
              Your 6-Step Blueprint to ₦947K+ Monthly Digital Income
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Book cover */}
            <div className="text-center">
              <div className="bg-white rounded-lg inline-block">
                <div className="rounded-lg p-6">
                  <img
                    src={bookCover}
                    alt="WEALTH manual cover"
                    className="w-48 mx-auto"
                  />
                </div>
                <p className="text-lg font-semibold opacity-90">
                  The Complete Manual That Changed Everything
                </p>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-6">
              {(wealthStepsData as WealthStep[]).map((step) => (
                <div
                  key={step.title}
                  className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300"
                >
                  <div className="flex items-center">
                    <div className="bg-white text-brandNavy rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                      {step.letter}
                    </div>
                    <div className="text-white mr-3 flex-shrink-0">
                      {iconMap[step.letter]}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-1">{step.title}</h4>
                      <p className="opacity-90 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Offer */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">
            Get The Complete WEALTH Method Today
          </h3>

          <div className="bg-gradient-to-br from-goldAccent to-goldAccent/80 rounded-lg shadow-xl p-8 border-2 border-lightBorder">
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-gray-800 mb-4">
                Here's everything you get:
              </h4>
            </div>

            <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
              <div className="flex items-center mb-4">
                <BookOpen className="w-8 h-8 text-brandNavy mr-4" />
                <div>
                  <h5 className="text-xl font-bold text-gray-800">
                    The WEALTH Method Complete Manual
                  </h5>
                  <p className="text-gray-600">
                    156 pages of step-by-step instructions
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <span className="text-2xl font-bold text-brandNavy">
                    ₦25,000
                  </span>
                </div>
              </div>

              <ul className="text-sm text-gray-700 space-y-1">
                <li>• 25 chapters of step-by-step instructions</li>
                <li>• Real Nigerian case studies and examples</li>
                <li>
                  • Chapter by chapter template for immediate implementation,
                  with practical examples.
                </li>
                <li>
                  • No guesswork. Everything simplified for you, and every
                  aspect covered. There are no assumptions at all.
                </li>
                <li>
                  • This is not just a book, it is a system and a manual for
                  your journey to your financial promise land.
                </li>
              </ul>
            </div>

            <div className="space-y-4 mb-8">
              {(bonusesData as Bonus[]).map((bonus, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-4 shadow-md border-l-4 border-brandNavy"
                >
                  <div className="flex items-center">
                    <div className="text-brandNavy mr-4">
                      {i === 0 && <BookOpen className="w-6 h-6" />}
                      {i === 1 && <MessageCircle className="w-6 h-6" />}
                      {i === 2 && <Shield className="w-6 h-6" />}
                      {i === 3 && <VideoIcon className="w-6 h-6" />}
                    </div>
                    <div className="flex-1">
                      <h6 className="font-bold text-gray-800">
                        BONUS #{i + 1}: {bonus.title}
                      </h6>
                      <p className="text-sm text-gray-600">
                        {bonus.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-brandNavy">
                        {bonus.value}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-800 text-white rounded-lg p-6 text-center">
              <div className="text-sm opacity-75 mb-2">TOTAL VALUE:</div>
              <div className="text-4xl font-bold mb-4">₦82,000</div>
              <div className="text-goldAccent text-lg mb-2">
                Your Investment Today:
              </div>
              <div className="text-5xl font-bold text-goldAccent">
                ₦{PRICE.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-16 px-4 bg-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-lightBorder">
            <Shield className="w-16 h-16 text-brandLightNavy mx-auto mb-6" />
            <h3 className="text-2xl font-bold mb-6 text-gray-800">
              Your Success is My Reputation
            </h3>
            <div className="bg-brandLightNavy bg-opacity-10 rounded-lg p-6 text-brandLightNavy">
              <p className="text-lg font-semibold mb-4">
                Iron-Clad 90-Day Guarantee:
              </p>
              <p className="text-base leading-relaxed">
                "Follow the system for the next 30 days. If you don't make at
                least ₦50,000 in digital income, I'll personally refund your
                ₦5,000 AND give you ₦5,000 extra for wasting your time."
              </p>
            </div>
            <p className="mt-6 text-gray-700">
              <strong>Why am I this confident?</strong> Because this system has
              worked for over 200 Nigerians already. It will work for you too,
              IF you work it.
            </p>
          </div>
        </div>
      </section>

      {/* Urgency */}
      <section className="py-16 px-4 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-8">
            Limited Monthly Spots Available
          </h3>
          <div className="bg-white bg-opacity-20 rounded-lg p-8 backdrop-blur-sm">
            <div className="text-4xl font-bold mb-4">
              Only {spotsLeft} Spots Left This Month
            </div>
            <div className="text-lg mb-6">
              We limit admission to {TOTAL_SPOTS} students monthly
            </div>

            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div>
                <h4 className="font-bold mb-3">
                  Why we limit monthly admission:
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• Personalized support for every student</li>
                  <li>• Quality over quantity approach</li>
                  <li>• Better results for smaller groups</li>
                  <li>• Direct access to mentorship</li>
                  <li>• Exclusive mastermind community</li>
                </ul>
              </div>
              <div className="bg-goldAccent text-black rounded-lg p-6">
                <h4 className="font-bold mb-3">The question is:</h4>
                <p className="text-sm leading-relaxed">
                  Will you secure your spot this month, or wait until next month
                  when spots open again?
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-brandNavy to-brandLightNavy text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-8">
            Secure Your Spot Right Now
          </h3>

          <div className="max-w-md mx-auto mb-12">
            <div className="bg-white text-gray-800 rounded-lg p-8 shadow-xl">
              <h4 className="text-2xl font-bold mb-4 text-brandNavy">
                Complete WEALTH Method Package
              </h4>
              <div className="text-sm text-gray-600 mb-4">
                Everything You Need to Start
              </div>
              <div className="text-5xl font-bold mb-6 text-brandNavy">
                ₦{PRICE.toLocaleString()}
              </div>

              <button
                onClick={handleClick}
                className="w-full bg-brandNavy text-white py-4 px-6 rounded-lg text-lg font-bold hover:bg-brandLightNavy transition-colors flex items-center justify-center mb-4"
                aria-label="Purchase the WEALTH method"
              >
                GET THE W.E.A.L.T.H METHOD NOW
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>

              <div className="flex items-center justify-center text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 mr-2" /> Secure Payment |
                Instant Download
              </div>
            </div>
          </div>

          <div className="bg-goldAccent text-black rounded-lg p-6 mb-8">
            <h4 className="text-xl font-bold mb-4">
              🚨 Final Reminder: Monthly Spots Are Limited 🚨
            </h4>
            <p className="leading-relaxed">
              In 12 months, you'll be somewhere. The question is WHERE? Still
              complaining about fuel prices and stuck in traffic, or running a
              ₦500,000+ monthly digital business from home?
            </p>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold mb-4">
              Your family is watching. Your future is waiting. Your time is NOW.
            </p>
            <button
              onClick={handleClick}
              className="bg-goldAccent text-black py-6 px-12 rounded-lg text-2xl font-bold hover:bg-goldAccent/90 transition-colors shadow-xl transform hover:scale-105"
            >
              🚨 GET THE W.E.A.L.T.H METHOD NOW - ₦{PRICE.toLocaleString()} 🚨
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brandNavy text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-sm space-y-4">
            <p>
              <strong>P.S.</strong> - Remember, we only accept {TOTAL_SPOTS} new
              students each month. Don't let another month pass while watching
              others succeed. Your breakthrough starts with this decision.
            </p>
            <p>
              <strong>P.P.S.</strong> - The WhatsApp mastermind alone is worth
              more than the entire investment. Connect with like-minded
              Nigerians who are building wealth, not making excuses.
            </p>
          </div>

          <div className="text-center mt-8 pt-8 border-t border-lightBorder">
            <p className="text-sm text-white">
              © {brandData.year ?? new Date().getFullYear()} The WEALTH Method.
              All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WealthMethod;
